/**
 * Recurrence rule → occurrence dates generator for recurring polls.
 *
 * The client generates the occurrence dates and stores them as the master
 * poll's selectedDates; the create_recurring_series RPC then builds the
 * instances overlay from those dates. Keep this logic in sync with the spec:
 * Docs/SPECS/2026-07-06-recurring-polls.md
 *
 * All date math is done in UTC on YYYY-MM-DD strings — never via
 * new Date("YYYY-MM-DD") local-time parsing (DST/TZ shift bugs).
 */

export type RecurrenceFreq =
  | "daily"
  | "weekly"
  | "biweekly"
  | "monthly"
  | "custom";

export interface RecurrenceRule {
  freq: RecurrenceFreq;
  /** First occurrence, YYYY-MM-DD. Required unless freq === "custom". */
  dtstart?: string;
  /** Last possible occurrence (inclusive), YYYY-MM-DD. Required unless freq === "custom". */
  until?: string;
  /** Hand-picked dates, YYYY-MM-DD. Only for freq === "custom". */
  dates?: string[];
}

/** Hard cap from spec: a series never exceeds one year of weekly occurrences. */
export const MAX_RECURRING_OCCURRENCES = 52;

/** Minimum occurrences for a series to make sense. */
export const MIN_RECURRING_OCCURRENCES = 2;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseUtc(iso: string): number {
  if (!ISO_DATE_RE.test(iso)) {
    throw new RecurrenceError("INVALID_DATE_FORMAT", `Invalid date: ${iso}`);
  }
  const [y, m, d] = iso.split("-").map(Number);
  const ts = Date.UTC(y, m - 1, d);
  const check = new Date(ts);
  if (
    check.getUTCFullYear() !== y ||
    check.getUTCMonth() !== m - 1 ||
    check.getUTCDate() !== d
  ) {
    throw new RecurrenceError("INVALID_DATE_FORMAT", `Invalid date: ${iso}`);
  }
  return ts;
}

function toIso(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

const DAY_MS = 24 * 60 * 60 * 1000;

export class RecurrenceError extends Error {
  constructor(
    public readonly code:
      | "INVALID_DATE_FORMAT"
      | "INVALID_RULE"
      | "TOO_MANY_OCCURRENCES"
      | "TOO_FEW_OCCURRENCES",
    message: string,
  ) {
    super(message);
    this.name = "RecurrenceError";
  }
}

/**
 * Generates the sorted, de-duplicated list of occurrence dates for a rule.
 * Throws RecurrenceError when the rule is invalid or the occurrence count is
 * outside [MIN_RECURRING_OCCURRENCES, MAX_RECURRING_OCCURRENCES].
 */
export function generateDatesFromRule(rule: RecurrenceRule): string[] {
  let dates: string[];

  if (rule.freq === "custom") {
    if (!rule.dates || rule.dates.length === 0) {
      throw new RecurrenceError("INVALID_RULE", "custom rule requires dates[]");
    }
    dates = [...new Set(rule.dates.map((d) => toIso(parseUtc(d))))].sort();
  } else {
    if (!rule.dtstart || !rule.until) {
      throw new RecurrenceError(
        "INVALID_RULE",
        `${rule.freq} rule requires dtstart and until`,
      );
    }
    const start = parseUtc(rule.dtstart);
    const until = parseUtc(rule.until);
    if (until < start) {
      throw new RecurrenceError("INVALID_RULE", "until must be >= dtstart");
    }

    dates = [];
    if (rule.freq === "monthly") {
      // Same day-of-month as dtstart; months lacking that day are skipped
      // (e.g. a series starting Jan 31 has no February occurrence).
      const startDate = new Date(start);
      const dayOfMonth = startDate.getUTCDate();
      let year = startDate.getUTCFullYear();
      let month = startDate.getUTCMonth();
      // Iterate months, bounded well past the 52-occurrence cap.
      for (let i = 0; i < MAX_RECURRING_OCCURRENCES * 2; i++) {
        const ts = Date.UTC(year, month, dayOfMonth);
        const candidate = new Date(ts);
        if (ts > until) break;
        if (candidate.getUTCDate() === dayOfMonth) {
          dates.push(toIso(ts));
          if (dates.length > MAX_RECURRING_OCCURRENCES) break;
        }
        month += 1;
        if (month > 11) {
          month = 0;
          year += 1;
        }
      }
    } else {
      const stepDays =
        rule.freq === "daily" ? 1 : rule.freq === "weekly" ? 7 : 14;
      // `<=` (pas `<`) : on génère volontairement UNE occurrence au-delà du cap
      // pour que le check final lève TOO_MANY_OCCURRENCES. Avec `<`, une règle
      // qui en produit 60 retournerait 52 en silence (troncature invisible).
      for (
        let ts = start;
        ts <= until && dates.length <= MAX_RECURRING_OCCURRENCES;
        ts += stepDays * DAY_MS
      ) {
        dates.push(toIso(ts));
      }
    }
  }

  if (dates.length > MAX_RECURRING_OCCURRENCES) {
    throw new RecurrenceError(
      "TOO_MANY_OCCURRENCES",
      `Rule generates more than ${MAX_RECURRING_OCCURRENCES} occurrences`,
    );
  }
  if (dates.length < MIN_RECURRING_OCCURRENCES) {
    throw new RecurrenceError(
      "TOO_FEW_OCCURRENCES",
      `Rule generates fewer than ${MIN_RECURRING_OCCURRENCES} occurrences`,
    );
  }
  return dates;
}

/**
 * Next active occurrence strictly on/after `today` (YYYY-MM-DD).
 * Returns null when the series is over.
 */
export function nextOccurrence(
  instanceDates: Array<{ occurrence_date: string; status: string }>,
  today: string,
): { occurrence_date: string; status: string } | null {
  const todayTs = parseUtc(today);
  const upcoming = instanceDates
    .filter(
      (i) => i.status === "active" && parseUtc(i.occurrence_date) >= todayTs,
    )
    .sort((a, b) => a.occurrence_date.localeCompare(b.occurrence_date));
  return upcoming[0] ?? null;
}
